import React from 'react';

import clsx from 'classnames';
import PropTypes from 'prop-types';

import { combineClasses } from 'wanderu-css';

import ButtonBase from '../ButtonBase/ButtonBase';
import ListContext from '../List/ListContext';
import ListAdornment from '../ListAdornment/ListAdornment';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './ListItem.scss';

function ListItem(props) {
    const {
        button = false,
        children,
        classes: classesProp = {},
        className,
        component: ComponentProp = 'li',
        componentProps: componentPropsRaw,
        spacing: spacingProp = undefined, // this must be undefined or it overrides the value in the context
        ...otherProps
    } = props;

    const hasLeftAdornment = children[0]?.type === ListAdornment;
    const rightAdornmentIndex = hasLeftAdornment
        ? 2
        : 1;
    const hasRightAdornment = children[rightAdornmentIndex]?.type === ListAdornment;

    const { spacing: spacingContext } = React.useContext(ListContext);
    let spacing = spacingProp || spacingContext || 'auto';

    if (spacingProp === 0 || spacingContext === 0) {
        spacing = 0;
    }

    let Component = ComponentProp;
    let componentProps = componentPropsRaw || {};
    if (button) {
        Component = ButtonBase;
        componentProps.component = ComponentProp;
    }

    const classes = combineClasses(styles, classesProp);

    return (
        <Component
            className={clsx(
                classes.root,
                {
                    [classes.button]: button,
                    [classes.leftAdornment]: hasLeftAdornment && !hasRightAdornment,
                    [classes.rightAdornment]: hasRightAdornment && !hasLeftAdornment,
                    [classes.bothAdornment]: hasRightAdornment && hasLeftAdornment,
                    [classes[`spacing-${spacing}`]]: spacing !== 2,
                },
                className,
            )}
            {...otherProps}
            {...componentProps}
        >
            {children}
        </Component>
    );
}

ListItem.propTypes = {
    /**
     * If `true`, the list item will be a button (using `ButtonBase`). Props intended
     * for `ButtonBase` can then be applied to `ListItem`.
     */
    button: PropTypes.bool,
    /**
     * The content of the list item.
     */
    children: PropTypes.node.isRequired,
    /**
     * Override or extend the styles applied to the component.
     */
    classes: PropTypes.object,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * The component used for the root node.
     * Either a string to use a HTML element or a component.
     */
    component: PropTypes.elementType,
    /**
     * The set of components passed down to the root component
     */
    componentProps: PropTypes.object,
    /**
     * Defines the gutter size of n * the base spacing size between the list parts.
     * Can be control via context from the <List /> component
     */
    spacing: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
};

export default ListItem;
